import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { buildWhatsAppSavePayload, validateWhatsAppSetupForm } from '../../helpers/whatsapp';
import { mapWhatsAppApiError, saveSettings } from '../../services/whatsapp';

const EMPTY_FORM = {
  access_token: '',
  phone_number_id: '',
  waba_id: '',
  app_id: '',
  app_secret: '',
  webhook_verify_token: '',
};

const SAVED_SECRET_PLACEHOLDER = 'Already saved — leave blank to keep';

const WhatsAppSetupModal = ({ show, settings, onCancel, onSaved }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    if (!show) {
      setForm(EMPTY_FORM);
      setErrors({});
      setApiError('');
      setSaving(false);
      return;
    }
    setForm({
      ...EMPTY_FORM,
      phone_number_id: settings?.phoneNumberId ? String(settings.phoneNumberId) : '',
      waba_id: settings?.wabaId ? String(settings.wabaId) : '',
      app_id: settings?.appId ? String(settings.appId) : '',
    });
    setErrors({});
    setApiError('');
    setSaving(false);
  }, [show, settings]);

  const setField = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleHide = () => {
    if (!saving) onCancel();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const nextErrors = validateWhatsAppSetupForm(form, settings);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaving(true);
    setApiError('');
    try {
      await saveSettings(buildWhatsAppSavePayload(form));
      onSaved();
    } catch (err) {
      setApiError(mapWhatsAppApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleHide} centered className="wa-setup-modal">
      <Modal.Header closeButton={!saving}>
        <Modal.Title>WhatsApp Business Setup</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit} autoComplete="off">
        <Modal.Body>
          <p className="text-muted small mb-3">
            Credentials are stored per tenant on the server. Secrets are never returned to the browser.
          </p>

          {apiError && (
            <Alert variant="danger" className="py-2 mb-3">
              {apiError}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label className="fw-medium">Meta Access Token</Form.Label>
            <Form.Control
              className="wa-input"
              type="password"
              autoComplete="new-password"
              value={form.access_token}
              onChange={setField('access_token')}
              isInvalid={!!errors.access_token}
              disabled={saving}
              placeholder={settings?.hasAccessToken ? SAVED_SECRET_PLACEHOLDER : undefined}
            />
            <Form.Control.Feedback type="invalid">{errors.access_token}</Form.Control.Feedback>
            <Form.Text className="text-muted">From Meta Developer → WhatsApp → API Setup</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-medium">Phone Number ID</Form.Label>
            <Form.Control
              className="wa-input"
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={form.phone_number_id}
              onChange={setField('phone_number_id')}
              isInvalid={!!errors.phone_number_id}
              disabled={saving}
            />
            <Form.Control.Feedback type="invalid">{errors.phone_number_id}</Form.Control.Feedback>
            <Form.Text className="text-muted">From Meta Business / WhatsApp Manager → Phone numbers</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-medium">WABA ID (Business Account)</Form.Label>
            <Form.Control
              className="wa-input"
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={form.waba_id}
              onChange={setField('waba_id')}
              isInvalid={!!errors.waba_id}
              disabled={saving}
            />
            <Form.Control.Feedback type="invalid">{errors.waba_id}</Form.Control.Feedback>
            <Form.Text className="text-muted">WhatsApp Business Account ID from Meta Business Manager</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-medium">Meta App ID</Form.Label>
            <Form.Control
              className="wa-input"
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={form.app_id}
              onChange={setField('app_id')}
              disabled={saving}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-medium">Meta App Secret</Form.Label>
            <Form.Control
              className="wa-input"
              type="password"
              autoComplete="new-password"
              value={form.app_secret}
              onChange={setField('app_secret')}
              disabled={saving}
              placeholder={settings?.hasAppSecret ? SAVED_SECRET_PLACEHOLDER : undefined}
            />
            <Form.Text className="text-muted">Recommended for webhook signature validation</Form.Text>
          </Form.Group>

          <Form.Group className="mb-0">
            <Form.Label className="fw-medium">Webhook Verify Token</Form.Label>
            <Form.Control
              className="wa-input"
              type="password"
              autoComplete="new-password"
              value={form.webhook_verify_token}
              onChange={setField('webhook_verify_token')}
              disabled={saving}
              placeholder={settings?.hasWebhookVerifyToken ? SAVED_SECRET_PLACEHOLDER : undefined}
            />
            <Form.Text className="text-muted">Custom verify token for Meta webhook subscription</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button className="wa-btn-secondary" type="button" onClick={handleHide} disabled={saving}>
            Cancel
          </Button>
          <Button className="btn wa-btn-primary" type="submit" disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" className="me-1" />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default WhatsAppSetupModal;
